import {
  minMax,
  multiselect,
  title,
  select,
  autocomplete,
  dateRange,
} from './filter-builder';

const filterSet = (filters) => ({
  exclude = [],
  append = [],
  overrides = {},
} = {}) => [
  ...filters
    .filter(({ key }) => !exclude.includes(key))
    .map((filter) => ({
      ...filter,
      ...(overrides[filter.key] || filter),
    })),
  ...append,
];

export const transactionFilters = filterSet([
  title('Transactions'),
  multiselect({
    source: 'transactions',
    key: 'transaction_type',
    label: 'Transaction type',
  }),
  multiselect({
    source: 'transactions',
    key: 'parcel_id',
    label: 'Has Transaction',
    operator: 'is_defined',
    options: [
      {
        label: 'Pick a filter',
        value: 'EMPTY',
      },
      {
        value: 'any',
        label: 'Yes',
      },
    ],
  }),
  dateRange({
    source: 'transactions',
    key: 'sale_date',
    label: 'Sale Date',
  }),
  minMax({
    source: 'transactions',
    key: 'amount',
    label: 'Sale Amount',
    unit: 'DOLLARS',
  }),
  minMax({
    source: 'transactions',
    key: 'num_props_in_sale',
    label: 'Number or properties in sale',
  }),
  multiselect({
    source: 'transactions',
    key: 'multiproperty_sale',
    label: 'Multi-property sale',
    options: [
      {
        label: 'Pick a filter',
        value: 'EMPTY',
      },
      {
        value: 1,
        label: 'Multiple Properties',
      },
      {
        value: 0,
        label: 'Single Property',
      },
    ],
  }),
  minMax({
    source: 'transactions',
    key: 'price_per_sq_ft',
    label: 'Price per sq. foot',
    unit: 'DOLLARS',
  }),
  multiselect({
    source: 'transactions',
    key: 'transfer_type_descr',
    label: 'Transaction type description',
  }),
  multiselect({
    source: 'transactions',
    key: 'deed_type',
    label: 'Transaction Deed Type',
  }),

  autocomplete({
    source: 'transactions',
    key: 'grantor',
    label: 'Grantor',
  }),
  autocomplete({
    source: 'transactions',
    key: 'grantee',
    label: 'Grantee',
  }),
]);

export const saleAndForeclosureFilters = filterSet([
  title('Sales and Foreclosures'),
  select({
    key: 'mortgage_foreclosure',
    label: 'Mortgage-Foreclosured in the Last Year',
  }),
  select({
    key: 'tax_foreclosure',
    label: 'Tax-Foreclosured in the Last Year',
  }),
  minMax({
    key: 'res_sp_sqft',
    label: 'Area Current Residential Price/Sq. Foot',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'res_sp_sqft_1y',
    label: 'Area Change in Residential Price Per Sq. Foot - 1 year',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'res_sp_sqft_5y',
    label: 'Area Change in Residential Price Per Sq. Foot - 5 year',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'tax_foreclosure_hex',
    label: 'Area Percent Tax Foreclosure',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'tax_foreclosure_1y',
    label: 'Area Change in Tax Foreclosure - 1 year',
  }),
  minMax({
    key: 'tax_foreclosure_5y',
    label: 'Area Change in Tax Foreclosure - 5 year',
  }),
  minMax({
    key: 'mortgage_foreclosure_hex',
    label: 'Area Percent Mortgage Foreclosure',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'mortgage_foreclosure_1y',
    label: 'Area Change in Mortgage Foreclosure - 1 year',
  }),
  minMax({
    key: 'mortgage_foreclosure_5y',
    label: 'Area Change in Mortgage Foreclosure - 5 year',
  }),
]);

export const ownershipAndOccupancyFilters = filterSet([
  title('Ownership & Occupancy'),
  select({
    key: 'is_vacant',
    label: 'Current Occupancy Status',
    options: [
      {
        label: 'Pick a filter',
        value: 'EMPTY',
      },
      {
        value: 1,
        label: 'Unoccupied',
      },
      {
        value: 0,
        label: 'Occupied',
      },
    ],
  }),
  multiselect({
    key: 'vacancy_detector',
    label: 'Vacated',
  }),
  multiselect({
    key: 'reoccupancy_detector',
    label: 'Re-Occupied',
  }),
  select({
    key: 'owner_occupancy',
    label: 'Current Owner-Occupancy Status',
    options: [
      {
        label: 'Pick a filter',
        value: 'EMPTY',
      },
      {
        value: 1,
        label: 'Owner Occupied',
      },
      {
        value: 0,
        label: 'Not Owner Occupied',
      },
    ],
  }),
  autocomplete({
    key: 'owner_name',
    label: 'Owner Name',
  }),
  autocomplete({
    key: 'owner_address_full',
    label: 'Owner Address',
  }),
  minMax({
    key: 'owner_prop_count',
    label: 'Other properties owned across the city',
  }),
  minMax({
    key: 'occupied',
    label: 'Area Current Occupancy Rate',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'occupied_1y',
    label: 'Area Change in Occupancy - 1 year',
  }),
  minMax({
    key: 'occupied_5y',
    label: 'Area Change in Occupancy - 5 year',
  }),
  minMax({
    key: 'owner_occ',
    label: 'Area Current Owner-Occupancy Rate',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'owner_occ_1y',
    label: 'Area Change in Owner Occupancy - 1 year',
  }),
  minMax({
    key: 'owner_occ_5y',
    label: 'Area Change in Owner Occupancy - 5 year',
  }),
]);

export const propertyTaxes = filterSet([
  title('Property Taxes'),

  multiselect({
    key: 'tax_status',
    label: 'Current Tax Status',
  }),
  minMax({
    key: 'total_owed',
    label: 'Total Taxes Owed',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'total_paid',
    label: 'Total Taxes Paid',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'total_balance',
    label: 'Total Tax Balance',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'delq_balance',
    label: 'Delinquent Tax Balance',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'tax_delinquent',
    label: 'Area Current Tax Delinquency Rate',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'tax_delinquent_1y',
    label: 'Area Change in Tax Delinquency - 1 year',
  }),
  minMax({
    key: 'tax_delinquent_5y',
    label: 'Area Change in Tax Delinquency - 5 year',
  }),
]);

export const jacksonCodeEnforcementFilters = filterSet([
  title('Code Enforcement'),
  multiselect({
    key: 'rental_license',
    label: 'Rental Permit or Certificate',
  }),
  minMax({
    key: 'open_permit_count',
    label: 'Open permits count',
  }),
  multiselect({
    key: 'open_violation_type',
    label: 'Violation Type',
    operator: 'contains',
    options: [
      {
        value: 'AHB',
        label: 'AHB',
      },
      {
        value: 'AHB NOTICE ISSUED',
        label: 'AHB NOTICE ISSUED',
      },
      {
        value: 'BLDG CODE VIOLATION',
        label: 'BLDG CODE VIOLATION',
      },
      {
        value: 'Code Enforcement Exterior (CEX)',
        label: 'Code Enforcement Exterior (CEX)',
      },
      {
        value: 'DANGEROUS BUILDING',
        label: 'DANGEROUS BUILDING',
      },
      {
        value: 'DONATION BOXES',
        label: 'DONATION BOXES',
      },
      {
        value: 'GARBAGE & DEBRIS',
        label: 'GARBAGE & DEBRIS',
      },
      {
        value: 'GARBAGE AND DEBRIS',
        label: 'GARBAGE AND DEBRIS',
      },
      {
        value: 'GRAFFITI',
        label: 'GRAFFITI',
      },
      {
        value: 'INOPERABLE VEHICLE',
        label: 'INOPERABLE VEHICLE',
      },
      {
        value: 'J-Code Notes',
        label: 'J-Code Notes',
      },
      {
        value: 'NON-RESIDENTIAL',
        label: 'NON-RESIDENTIAL',
      },
      {
        value: 'NOTICE TO VACATE',
        label: 'NOTICE TO VACATE',
      },
      {
        value: 'PARKWAY/ROW',
        label: 'PARKWAY/ROW',
      },
      {
        value: 'TENANT COMPLAINT',
        label: 'TENANT COMPLAINT',
      },
      {
        value: 'TREE ORDINANCE',
        label: 'TREE ORDINANCE',
      },
      {
        value: 'WORK WITHOUT PERMIT',
        label: 'WORK WITHOUT PERMIT',
      },
      {
        value: 'ZONING ORDINANCE',
        label: 'ZONING ORDINANCE',
      },
    ],
  }),
  minMax({
    key: 'open_violation_count',
    label: 'Open violations (Enforcement count)',
    unit: 'INTEGER',
  }),
  minMax({
    key: 'violation_2y_count',
    label:
      'Violations since last change of ownership at this property or in the past two years',
  }),
  minMax({
    key: 'violation_owner_2y',
    label:
      'Violations at other properties owned by owner in past 2 years or since ownership',
  }),
  multiselect({
    key: 'changeability',
    label: 'Changeability',
  }),
]);

export const codeEnforcementFilters = filterSet([
  title('Code Enforcement'),
  minMax({
    key: 'open_permit_count',
    label: 'Open Permit Count',
  }),
  multiselect({
    key: 'rental_license',
    label: 'Rental Permit or Certificate',
  }),
  multiselect({
    key: 'open_violation_type',
    label: 'Violation Type',
    operator: 'contains',
    options: [
      {
        value: 'BUILDING MISC.',
        label: 'BUILDING MISC.',
      },
      {
        value: 'DEMOLITION OF STRUCTURES',
        label: 'DEMOLITION OF STRUCTURES',
      },
      {
        value: 'Elec Code',
        label: 'Elec Code',
      },
      {
        value: 'FINAL VIOLATION LIST',
        label: 'FINAL VIOLATION LIST',
      },
      {
        value: 'HOUSING CODE 2014',
        label: 'HOUSING CODE 2014',
      },
      {
        value: 'Housing Code',
        label: 'Housing Code',
      },
      {
        value: 'HOUSING CODE',
        label: 'HOUSING CODE',
      },
      {
        value: 'ORDINANCE',
        label: 'ORDINANCE',
      },
      {
        value: 'Zoning',
        label: 'Zoning',
      },
    ],
  }),
  minMax({
    key: 'open_violation_count',
    label: 'Open violations (Enforcement count)',
    unit: 'INTEGER',
  }),
  minMax({
    key: 'violation_2y_count',
    label:
      'Violations since last change of ownership at this property or in the past two years',
    unit: 'INTEGER',
  }),
  minMax({
    key: 'violation_owner_2y',
    label:
      'Violations at other properties owned by owner in past 2 years or since ownership',
    unit: 'INTEGER',
  }),
]);

export const zoningDevelopment = filterSet([
  title('Zoning & Development'),
  multiselect({
    key: 'general_zoning',
    label: 'General Zoning',
  }),
  multiselect({
    key: 'zoning',
    label: 'Zoning',
  }),
  multiselect({
    key: 'land_use',
    label: 'Current Use',
  }),
  minMax({
    key: 'year_built',
    label: 'Year Built',
  }),
  minMax({
    key: 'all_sqft',
    label: 'Area Building Density (sq. ft. per hexagon)',
  }),
  minMax({
    key: 'count',
    label: 'Area Density (parcels per hexagon)',
  }),
  minMax({
    key: 'vacant_lot',
    label: 'Area Current Vacant Lot Rate',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'vacant_lot_1y',
    label: 'Area Current Vacant Lot - 1 year',
  }),
  minMax({
    key: 'vacant_lot_5y',
    label: 'Area Current Vacant Lot - 5 year',
  }),
]);

export const neighborhoodRedevelopment = filterSet([
  title('Neighborhood Revitalization'),
  minMax({
    key: 'rehab_predict_impact',
    label: 'Predicted Rehabilitation Impact',
    unit: 'DOLLARS',
  }),
  multiselect({
    key: 'rehab',
    label: 'Rehabilitation Completed',
  }),
  minMax({
    key: 'rehab_impact',
    label: 'Completed Rehabilitation Impact',
    unit: 'DOLLARS',
  }),
  dateRange({
    key: 'rehab_date',
    label: 'Rehabilitation Date',
  }),
  minMax({
    key: 'demo_predict_impact',
    label: 'Predicted Demolition Impact',
    unit: 'DOLLARS',
  }),
  multiselect({
    key: 'demo',
    label: 'Demolition Completed',
  }),
  minMax({
    key: 'demo_impact',
    label: 'Completed Demolition Impact',
    unit: 'DOLLARS',
  }),
  dateRange({
    key: 'demo_date',
    label: 'Demolition Date',
  }),
]);

export const placeImpact = filterSet([
  title('Place Impact'),
  minMax({
    key: 'place_impact_1m',
    label: 'Predicted Place Impact at 1M',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'place_impact_10m',
    label: 'Predicted Place Impact at 10M',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'res_place_impact_1m',
    label: 'Predicted Res. Place Impact at 1M',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'res_place_impact_10m',
    label: 'Predicted Res. Place Impact at 10M',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'com_place_impact_1m',
    label: 'Predicted Com. Place Impact at 1M',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'com_place_impact_10m',
    label: 'Predicted Com. Place Impact at 10M',
    unit: 'DOLLARS',
  }),

  minMax({
    key: 'impact_total_place',
    label: 'Completed Place Impact',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'impact_residential',
    label: 'Completed Place Impact on Res.',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'impact_commercial',
    label: 'Completed Place Impact on Com.',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'bcr',
    label: 'BCR per $1',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'impact_properties',
    label: 'Number of Impacted Parcels',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'impact_average',
    label: 'Average Impact Per Affected Parcel',
    unit: 'DOLLARS',
  }),
  multiselect({
    key: 'program',
    label: 'Intervention Program',
  }),
  multiselect({
    key: 'organization',
    label: 'Organization',
  }),
  multiselect({
    key: 'purpose',
    label: 'Intervention Purpose',
  }),
  multiselect({
    key: 'line_of_business',
    label: 'Intervention Line of Business',
  }),
]);

export const socioEconomicDemographicsFilters = filterSet([
  title('Socio-Economic & Demographics'),
  minMax({
    key: 'med_hh_inc',
    label: 'Median Household Income',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'pov_pct',
    label: 'Poverty Rate',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'ed_ba_plus',
    label: "Bachelor's Degree Holder Rate",
    unit: 'PERCENT',
  }),
  minMax({
    key: 'med_rent',
    label: 'Median Rent',
    unit: 'DOLLARS',
  }),
  minMax({
    key: 'hh_w_kids',
    label: 'Households with Children Under 18',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_white',
    label: '% of Population White',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_afam',
    label: '% of Population Black or African American',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_natv',
    label: '% of Population American Indian or Alaska Native',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_asian',
    label: '% of Population Asian',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_islnd',
    label: '% of Population Native Hawaiian or Other Pacific Islander',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_other',
    label: '% of Population Other Race',
    unit: 'PERCENT',
  }),
  minMax({
    key: 'rc_2p',
    label: '% of Population Two or More Races',
    unit: 'PERCENT',
  }),
]);

export const parcelFilters = filterSet([
  title('Parcel'),
  autocomplete({
    key: 'parcel_id+autocomplete',
    label: 'Parcel Id',
  }),
  autocomplete({
    key: 'address+autocomplete',
    label: 'Address',
  }),
  select({
    key: 'structure',
    label: 'Has Structure',
  }),
  minMax({
    key: 'beds',
    label: 'Number of Bedrooms',
  }),
  minMax({
    key: 'baths',
    label: 'Number of Bathrooms',
  }),
  minMax({
    key: 'residential_area',
    label: 'Residential Sq. Feet',
  }),
  multiselect({
    key: 'zip',
    label: 'Zip Code',
  }),
]);
